import { useState, useLayoutEffect, useRef } from 'react'

// Ширина/высота — в CSS-пикселях (не в физических пикселях матрицы).
// Одна ширина может быть у разных моделей — оставлены самые ходовые представители.
const SIZES = [
  // Старые iPhone
  { group: 'iPhone', label: 'SE 1', width: 320, height: 568 },
  { group: 'iPhone', label: 'SE 2/3 · 6–8', width: 375, height: 667 },
  { group: 'iPhone', label: '6–8 Plus', width: 414, height: 736 },
  { group: 'iPhone', label: 'X · XS · 13 mini', width: 375, height: 812 },
  { group: 'iPhone', label: 'XR · 11', width: 414, height: 896 },
  // Новые iPhone
  { group: 'iPhone', label: '12–14 · 16e', width: 390, height: 844 },
  { group: 'iPhone', label: '14 Pro · 15 · 16', width: 393, height: 852 },
  { group: 'iPhone', label: '16 Pro', width: 402, height: 874 },
  { group: 'iPhone', label: '12–14 Pro Max', width: 428, height: 926 },
  { group: 'iPhone', label: '15/16 Plus · Pro Max', width: 430, height: 932 },
  { group: 'iPhone', label: '16 Pro Max', width: 440, height: 956 },
  // Android
  { group: 'Android', label: 'Galaxy S8–S21', width: 360, height: 800 },
  { group: 'Android', label: 'Galaxy S23 Ultra', width: 384, height: 824 },
  { group: 'Android', label: 'Pixel 5', width: 393, height: 851 },
  { group: 'Android', label: 'Pixel 7/8', width: 412, height: 915 },
  // Крупные экраны
  { group: 'Экран', label: 'iPad', width: 768, height: null },
  { group: 'Экран', label: 'Laptop', width: 1280, height: null },
  { group: 'Экран', label: '1920px', width: 1920, height: 1080 },
  { group: 'Экран', label: 'Full', width: null, height: null },
]

type Size = typeof SIZES[number]

const GROUPS = ['iPhone', 'Android', 'Экран']

const STAGE_PADDING = 24

export function DevPreview({ children }: { children: React.ReactNode }) {
  const [active, setActive] = useState<Size | null>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [barH, setBarH] = useState(44)
  const isInsideIframe = window.self !== window.top

  // Тулбар переносится на несколько строк — его высота зависит от ширины окна.
  useLayoutEffect(() => {
    const el = barRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setBarH(el.offsetHeight))
    ro.observe(el)
    setBarH(el.offsetHeight)
    return () => ro.disconnect()
  }, [])

  // Если макет шире доступной области (напр. 1920 на меньшем экране) — масштабируем,
  // чтобы он влезал целиком и не появлялся горизонтальный скролл.
  useLayoutEffect(() => {
    if (!active?.width) {
      setScale(1)
      return
    }
    const update = () => {
      const avail = (stageRef.current?.clientWidth ?? window.innerWidth) - STAGE_PADDING * 2
      setScale(Math.min(1, avail / active.width!))
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [active])

  if (!import.meta.env.DEV || isInsideIframe) return <>{children}</>

  return (
    <div>
      {/* Toolbar — в обычном потоке, не fixed: иначе перекрывает sticky-хедер сайта при скролле */}
      <div
        ref={barRef}
        style={{
          background: '#18181b', boxShadow: '0 1px 0 rgba(255,255,255,0.08)',
          display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 16px',
        }}
      >
        {GROUPS.map(group => (
          <div key={group} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
            <span style={{
              color: '#52525b', fontSize: 11, fontFamily: 'monospace', letterSpacing: 1,
              width: 64, flexShrink: 0,
            }}>
              {group.toUpperCase()}
            </span>
            {SIZES.filter(s => s.group === group).map(s => (
              <button
                key={s.label}
                title={s.width ? `${s.width}×${s.height ?? '—'}` : 'Без ограничения ширины'}
                onClick={() => setActive(s.width ? s : null)}
                style={{
                  padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                  fontSize: 12, fontFamily: 'monospace', whiteSpace: 'nowrap',
                  background: active?.label === s.label ? '#3b7eff' : '#27272a',
                  color: active?.label === s.label ? '#fff' : '#a1a1aa',
                }}
              >
                {s.label}{s.width ? ` · ${s.width}` : ''}
                {active?.label === s.label && scale < 1 ? ` · ${Math.round(scale * 100)}%` : ''}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Content or iframe */}
      {active ? (
        <div
          ref={stageRef}
          style={{
            minHeight: `calc(100vh - ${barH}px)`, background: '#09090b',
            display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
            padding: `${STAGE_PADDING}px ${STAGE_PADDING}px 48px`,
            overflowX: 'hidden', overflowY: 'auto',
          }}
        >
          {scale < 1 ? (
            // Масштабированный макет: обёртка занимает уже уменьшенный размер
            <div style={{ flexShrink: 0, width: active.width! * scale, height: (active.height ?? 1080) * scale }}>
              <iframe
                key={active.label}
                src={window.location.pathname}
                width={active.width!}
                style={{
                  // content-box: иначе Tailwind-preflight (border-box) вычтет рамку из вьюпорта iframe
                  boxSizing: 'content-box',
                  border: '1px solid #27272a', borderRadius: 12, background: '#fff', display: 'block',
                  height: active.height ?? 1080,
                  transform: `scale(${scale})`, transformOrigin: 'top left',
                }}
              />
            </div>
          ) : (
            <iframe
              key={active.label}
              src={window.location.pathname}
              width={active.width!}
              style={{
                boxSizing: 'content-box',
                border: '1px solid #27272a', borderRadius: 12, background: '#fff', flexShrink: 0,
                height: active.height ? active.height : `calc(100vh - ${barH + STAGE_PADDING * 2}px)`,
              }}
            />
          )}
        </div>
      ) : children}
    </div>
  )
}
